import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isMFAChallenge,
  type LoginResult,
  type MFAChallenge,
  type Session,
} from '@rootsight/shared';
import { Alert, Button, CenteredPage, LanguageSwitcher, Logo, Stack } from '@rootsight/ui';

import { CONSOLE_URL } from '../api';

import { CodesStep } from './login/CodesStep';
import { EnrollStep } from './login/EnrollStep';
import { PasswordStep } from './login/PasswordStep';
import { SsoStep } from './login/SsoStep';
import { VerifyStep } from './login/VerifyStep';

/** SSO_ERROR_CODES are the failure codes the auth service puts in /?error=. */
const SSO_ERROR_CODES = [
  'sso_no_account',
  'sso_canceled',
  'sso_unavailable',
  'sso_failed',
  'sso_required',
] as const;
type SsoErrorCode = (typeof SSO_ERROR_CODES)[number];

function isSsoErrorCode(value: string): value is SsoErrorCode {
  return (SSO_ERROR_CODES as readonly string[]).includes(value);
}

interface LoginPageProps {
  /** The ?error= code a failed provider sign-in redirected back with. Unknown
   * codes get the generic message. */
  ssoError?: string | undefined;
}

/** Step is where the sign-in is: the password, a second factor, the recovery
 * codes a first factor came with, or a notice to read before the console. */
type Step =
  | { kind: 'password' }
  | { kind: 'sso' }
  | { kind: 'mfa'; challenge: MFAChallenge }
  | { kind: 'codes'; codes: string[]; resetAt?: string | undefined }
  | { kind: 'notice'; resetAt: string };

/** goToConsole hands the browser to the console SPA with a full navigation:
 * it is a separate bundle behind its own Traefik route. */
function goToConsole() {
  window.location.assign(CONSOLE_URL);
}

/**
 * LoginPage signs an operator in: the password, then, when their account or
 * their organization's policy asks for one, a second factor. Once there is a
 * session it hands the browser to the console, after anything the operator
 * must read first: recovery codes shown once, or that an administrator reset
 * their second factors.
 */
export function LoginPage({ ssoError }: LoginPageProps) {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const [step, setStep] = useState<Step>({ kind: 'password' });

  const afterSession = (session: Session & { recoveryCodes?: string[] }, resetAt?: string) => {
    const notice = session.mfaResetAt ?? resetAt;
    if (session.recoveryCodes && session.recoveryCodes.length > 0) {
      setStep({ kind: 'codes', codes: session.recoveryCodes, resetAt: notice });
    } else if (notice) {
      setStep({ kind: 'notice', resetAt: notice });
    } else {
      goToConsole();
    }
  };
  const onPassword = (result: LoginResult) => {
    if (isMFAChallenge(result)) setStep({ kind: 'mfa', challenge: result });
    else afterSession(result);
  };

  const resetAt =
    step.kind === 'mfa'
      ? step.challenge.mfaResetAt
      : step.kind === 'codes' || step.kind === 'notice'
        ? step.resetAt
        : undefined;
  const resetDate =
    resetAt &&
    new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'long' }).format(new Date(resetAt));

  return (
    <CenteredPage
      brand={<Logo name={t('appName', { ns: 'common' })} />}
      title={
        step.kind === 'mfa'
          ? t('login.mfa.title')
          : step.kind === 'sso'
            ? t('login.sso.ssoTitle')
            : t('login.title')
      }
      description={step.kind === 'password' ? t('login.subtitle') : undefined}
      footer={<LanguageSwitcher />}
    >
      <Stack gap={6}>
        {ssoError && step.kind === 'password' && (
          <Alert tone="danger" title={t('login.sso.errorTitle')}>
            {t(`login.sso.errors.${isSsoErrorCode(ssoError) ? ssoError : 'sso_failed'}`)}
          </Alert>
        )}
        {resetDate && (
          <Alert tone="warning" title={t('login.reset.title')}>
            {t('login.reset.body', { date: resetDate })}
          </Alert>
        )}
        {step.kind === 'password' && (
          <PasswordStep onResult={onPassword} onSso={() => setStep({ kind: 'sso' })} />
        )}
        {step.kind === 'sso' && <SsoStep onBack={() => setStep({ kind: 'password' })} />}
        {step.kind === 'mfa' && step.challenge.stage === 'verify' && (
          <VerifyStep
            challenge={step.challenge}
            onSession={(session) => afterSession(session, step.challenge.mfaResetAt)}
            onRestart={() => setStep({ kind: 'password' })}
          />
        )}
        {step.kind === 'mfa' && step.challenge.stage === 'enroll' && (
          <EnrollStep
            challenge={step.challenge}
            onSession={(session) => afterSession(session, step.challenge.mfaResetAt)}
          />
        )}
        {step.kind === 'codes' && <CodesStep codes={step.codes} onContinue={goToConsole} />}
        {step.kind === 'notice' && (
          <Button variant="primary" size="lg" onPress={goToConsole}>
            {t('login.reset.continue')}
          </Button>
        )}
      </Stack>
    </CenteredPage>
  );
}
